import type { TrpcClient } from "@common/api-interface/trpc-client";
import type { ProductBillingFrequency} from "@common/stores/purchase-store";
import { PurchaseStoreDependencies } from "@common/stores/purchase-store";
import { UserId } from "@common/types/user";
import { Purchases } from "@revenuecat/purchases-js";

let mockIsPremium = false;
export const mockPurchasesApi: PurchasesApi = {
  setUser: async () => {},
  getUserIsPremium: async () => mockIsPremium,
  getUserSubscriptionDetails: async () => null,
  getPurchaseOptions: async () => [
    {
      id: "subscription_monthly_1",
      name: "Billed Monthly",
      price: 3.99,
      monthlyPrice: 3.99,
      currency: "USD",
      billingFrequency: "monthly",
      displayPriceStr: "$3.99/mo",
    },
    {
      id: "subscription_annual_1",
      name: "Billed Yearly",
      price: 19.99,
      monthlyPrice: 1.67,
      currency: "USD",
      billingFrequency: "yearly",
      displayPriceStr: "$1.67/mo",
    },
    {
      id: "lifetime_1",
      name: "One Time Purchase",
      price: 39.98,
      monthlyPrice: null,
      currency: "USD",
      billingFrequency: "one-time",
      displayPriceStr: "$39.98",
    },
  ],
  buyProduct: async (productId: string, userEmail: string) => {
    mockIsPremium = true;
    return undefined;
  },
};

export function makePurchasesApi({ trpc }: { trpc: TrpcClient }) {
  const purchasesApi = {
    setUser: async (userId: string) => {
      try {
        if (!Purchases.isConfigured()) {
          Purchases.configure(process.env.NEXT_PUBLIC_REVENUECAT_API_KEY!, userId);
        } else {
          await Purchases.getSharedInstance().changeUser(userId);
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") return;
        throw error;
      }
    },
    getUserIsPremium: async () => {
      return trpc.users.getUserIsPremium.query();
    },
    getUserSubscriptionDetails: async () => {
      return trpc.users.getUserSubscriptionDetails.query();
    },
    getPurchaseOptions: async () => {
      try {
        const offerings = await Purchases.getSharedInstance().getOfferings();
        const currentOffering = offerings.current;
        if (!currentOffering) throw new Error("Could not find current offering");

        const availablePackages = offerings.current.availablePackages;

        const mapFrequency = (billingFrequency: string | null): ProductBillingFrequency => {
          if (billingFrequency === "P1M") return "monthly";
          if (billingFrequency === "P6M") return "half-yearly";
          if (billingFrequency === "P1Y") return "yearly";
          if (billingFrequency === null) return "one-time";
          throw new Error("Invalid billing frequency");
        };

        const billingFrequencyMonths = {
          monthly: 1,
          "half-yearly": 6,
          yearly: 12,
        };

        return availablePackages.map((pkg) => {
          const frequency = mapFrequency(pkg.rcBillingProduct.normalPeriodDuration);
          const monthlyPrice =
            frequency === "one-time"
              ? null
              : pkg.rcBillingProduct.currentPrice.amountMicros / 1000000 / billingFrequencyMonths[frequency];
          return {
            id: pkg.rcBillingProduct.identifier,
            name: pkg.rcBillingProduct.title,
            price: pkg.rcBillingProduct.currentPrice.amountMicros / 1000000,
            monthlyPrice,
            currency: pkg.rcBillingProduct.currentPrice.currency,
            billingFrequency: frequency,
            displayPriceStr: monthlyPrice
              ? `$${monthlyPrice}/mo`
              : `$${pkg.rcBillingProduct.currentPrice.amountMicros / 1000000}`,
          };
        });
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    buyProduct: async (productId: string, userEmail: string) => {
      try {
        const targetPackage = (await Purchases.getSharedInstance().getOfferings()).current?.availablePackages.find(
          (pkg) => pkg.rcBillingProduct.identifier === productId
        );
        if (!targetPackage) throw new Error(`Could not find package with id ${productId}`);
        const result = await Purchases.getSharedInstance().purchase({
          rcPackage: targetPackage,
          customerEmail: userEmail,
        });

        return result;
      } catch (error) {
        console.error(error);
      }
    },
  };

  return purchasesApi;
}

export type PurchasesApi = ReturnType<typeof makePurchasesApi>;
